import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Table } from 'antd';
import QuickEdit from '../../../components/assets/QuickEdit';
import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { Modal } from '../../../components/Modal';
import {
  AntModal,
  BorderedSection,
  Button,
  Spinner,
} from '../../../components';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FETCH_EXTERNAL_MITIGATION_MATRIX,
  UPDATE_EXTERNAL_MITIGATION_DIVISION,
  UPDATE_EXTERNAL_MITIGATION_PRODUCT,
  UPDATE_EXTERNAL_MITIGATION_PRODUCT_GROUP,
} from '../queries';
import { useMutation, useQuery } from '@apollo/client';
import { ExternalMitigationForm } from './forms/ExternalMitigationForm';
import ExternalIntermediate from './ExternalIntermediate';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Comment,
} from '../../../components/assets';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import { tableSortingMethods } from '../../../utils';
import QueryErrorBoundary from '../../../components/error-handling';
import { useUserContext } from '../../../context/PermissionsContext';
import PercentageFormatter from '../../../components/common/PercentageFormatter';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Vendor = {
  delay: number;
  vendorName: string;
  percentage: number;
  key: string;
  comment: string;
  externalMitigationUUID: string;
};

export type Product = {
  __typename:
    | 'MitigationDivision'
    | 'MitigationProductGroup'
    | 'MitigationProduct';
  locations?: Location[];
  vendors: Vendor[];
  name: string;
  revenue: number;
  key: string;
  children?: Product[];
};

type ExternalMitigationOptionDashboard = {
  division: Product;
};

interface DataReturned {
  externalMitigationOptionDashboard: ExternalMitigationOptionDashboard;
}

const columnsPerPage = 8;

function ExternalMitigationTable() {
  const [modal2Open, setModal2Open] = useState(false);
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionName = searchParams.get('division')!;
  const locationId = searchParams.get('location')!;
  const locationName = searchParams.get('locationName')!;
  const level = parseInt(searchParams.get('level')!);
  const divisionId = params.divisionId!;
  const [item, setItem] = useState<Product>();
  const [product, setProduct] = useState<
    'MitigationDivision' | 'MitigationProductGroup' | 'MitigationProduct' | ''
  >('');
  const [isEditing, setisEditing] = useState(false);

  const { canEdit } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_EXTERNAL_MITIGATION_MATRIX,
    {
      variables: {
        division: { uuid: divisionId },
        location: { uuid: locationId },
        level: level,
      },
    },
  );

  const [
    UpdateExternalMitigationDivision,
    { loading: updateExternalDivisionLoading },
  ] = useMutation(UPDATE_EXTERNAL_MITIGATION_DIVISION, {
    refetchQueries: ['GetInternalMitigationMatrix'],
  });

  const [
    updateExternalMitigationProductGroup,
    { loading: updateExternalProductGroupLoading },
  ] = useMutation(UPDATE_EXTERNAL_MITIGATION_PRODUCT_GROUP, {
    refetchQueries: ['GetInternalMitigationMatrix'],
  });

  const [
    updateExternalMitigationProduct,
    { loading: updateExternalProductLoading },
  ] = useMutation(UPDATE_EXTERNAL_MITIGATION_PRODUCT, {
    refetchQueries: ['GetInternalMitigationMatrix'],
  });

  const mutations = {
    MitigationDivision: UpdateExternalMitigationDivision,
    MitigationProductGroup: updateExternalMitigationProductGroup,
    MitigationProduct: updateExternalMitigationProduct,
  };

  const loadingStates =
    updateExternalDivisionLoading ||
    updateExternalProductGroupLoading ||
    updateExternalProductLoading;

  const transformedData = (data: DataReturned): Product[] => {
    if (data?.externalMitigationOptionDashboard?.division !== null) {
      return [data?.externalMitigationOptionDashboard?.division];
    } else {
      return [];
    }
  };

  const vendors: Vendor[] = transformedData(data!)[0]?.vendors || [];

  const handleClick = (record: Product): void => {
    setItem(record);
    setModal2Open(true);
    setProduct(record.__typename);
    setisEditing(true);
  };

  const handleCancel = () => {
    setModal2Open(false);
  };

  const handleAddComment = () => {
    setisEditing(false);
    setModal2Open(true);
    const { children, ...rest } = transformedData(data!)[0];
    setItem(rest);
  };

  const externalMitigation: Product[] = [
    data?.externalMitigationOptionDashboard?.division,
  ] as Product[];

  const flattenedVendors = vendors.map((vendor) => vendor.key);

  const vendorColumns: ColumnsType<Product> = (
    data?.externalMitigationOptionDashboard?.division?.vendors || []
  ).map((vendor, index: number) => ({
    title: (
      <DependenciesTitle name={vendor.vendorName} comment={vendor.comment} />
    ),
    dataIndex: 'vendors',
    key: vendor.key,
    width: 150,
    align: 'right',
    colIndex: index,
    editable: true,
    render: (_, record) => {
      const actualVendor = record.vendors?.find(
        (loc) => loc.key === vendor.key,
      );

      return (
        <div className="custom-table--column">
          <PercentageFormatter value={actualVendor?.percentage} />
          <span
            className={`${actualVendor?.delay === 0 ? 'text-[#CDCDCD]' : ''}`}
          >
            {actualVendor?.delay}
            {' mths'}
          </span>
        </div>
      );
    },
  }));

  const columns: ColumnsType<Product> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue Supported Annually',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 110,
      fixed: 'left',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.revenue, b.revenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    ...vendorColumns,
    // {
    //   title: '',
    //   key: 'action',
    //   fixed: 'right',
    //   width: 50,
    //   render: (_, record) => (
    //     <>
    //       {exposurePermissions && (
    //         <Button type="edit" onClick={() => handleClick(record)}>
    //           <QuickEdit />
    //         </Button>
    //       )}
    //     </>
    //   ),
    // },
  ];

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    const mutation =
      mutations[currentRecord.__typename as keyof typeof mutations];

    const commonValues = {
      location: {
        uuid: locationId,
      },
      mutation,
    };

    switch (currentRecord.__typename) {
      case 'MitigationDivision':
        return {
          ...commonValues,
          division: {
            uuid: currentRecord?.key,
          },
        };
      case 'MitigationProductGroup':
        return {
          ...commonValues,
          productGroup: {
            uuid: currentRecord?.key,
          },
        };
    }
    return {
      ...commonValues,
      product: {
        uuid: currentRecord?.key,
      },
    };
  };

  return (
    <div>
      <TitleUpdater title={`${locationName} External Mitigation`} />
      <TitleBar
        title="External Mitigation Options"
        subtitle={`${divisionName} // ${locationName}`}
        brief="Below is the list of products directly supported by this location, as well as any intermediates sent to
        locations you own. For each revenue stream, define what percentage could be supported by external locations /
        vendors (i.e. not owned by your company). As with the exposure quantification, this information can be provided
        at a high level (i.e. division) or at a product level, which makes the exposure quantification more accurate."
      />
      <div className="flex flex-col gap-6">
        <BorderedSection
          title="Finished Goods Mitigation"
          noPadding
          extendOptions={
            canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddComment}
                  className="text-blue underline hover:cursor-pointer font-normal"
                >
                  Add / Edit Commentary
                </button>
                <Comment />
              </div>
            ) : null
          }
        >
          <QueryErrorBoundary error={error}>
            <TableV3
              columns={columns}
              data={externalMitigation}
              loading={loading || loadingStates}
              editable
              modifyVariables={modifyVariables}
              customStyles="custom-table--dependency-mapping"
              inlineEditing
              useBorderedSection={false}
              editableOptions={{
                create: false,
                update: true,
                delete: false,
              }}
              isMatrix
              isMitigation
              scroll={{ x: 800, y: 340 }}
              mitigatedLocations={flattenedVendors}
            />
          </QueryErrorBoundary>
        </BorderedSection>
        <ExternalIntermediate location={locationId} division={divisionId} />
      </div>
      <Footer
        backPath={`/${divisionId}/external-mitigators?division=${divisionName}&location=${locationId}&locationName=${locationName}&level=${level}`}
      />
      <AntModal
        title="External Mitigation"
        openModal={modal2Open}
        onCancel={handleCancel}
      >
        <ExternalMitigationForm
          data={item}
          onCancel={handleCancel}
          product={product}
          divisionId={divisionId}
          locationId={locationId}
          isEditing={isEditing}
        />
      </AntModal>
    </div>
  );
}

export default ExternalMitigationTable;
